<template>
  <f7-popup class="popup-solicitar" :opened="opened" @popup:opened="abrir" @popup:closed="$emit('cerrar')">
    <f7-page>
      <f7-navbar :title="$t('misVacaciones.solicitarTitulo')">
        <f7-nav-right>
          <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
        </f7-nav-right>
      </f7-navbar>

      <!-- Uno o varios tramos en la misma solicitud. Cada cambio de fecha vuelve a preguntar al
           servidor: el cálculo no se hace aquí. -->
      <f7-block-title>{{ $t('misVacaciones.tramos') }}</f7-block-title>
      <f7-block strong class="tramos">
        <div v-for="(tramo, i) in tramos" :key="i" class="tramo-fila">
          <label class="campo">
            <span class="campo-label">{{ $t('misVacaciones.desde') }}</span>
            <input type="date" :value="tramo.desde" @change="fijar(i, 'desde', $event)" />
          </label>
          <label class="campo">
            <span class="campo-label">{{ $t('misVacaciones.hasta') }}</span>
            <input type="date" :value="tramo.hasta" @change="fijar(i, 'hasta', $event)" />
          </label>
          <f7-link v-if="tramos.length > 1" class="quitar" @click="quitarTramo(i)">
            {{ $t('misVacaciones.quitarTramo') }}
          </f7-link>
        </div>
        <f7-link class="anadir" @click="anadirTramo">+ {{ $t('misVacaciones.anadirTramo') }}</f7-link>
      </f7-block>

      <!-- Una fila a medias no se envía en silencio: el cálculo y el envío solo cuentan los
           periodos completos, así que se dice y «Enviar solicitud» se queda deshabilitado hasta
           que se complete o se quite. Solo cuando ya hay algún periodo completo: en el formulario
           recién abierto no hay nada de lo que avisar. -->
      <f7-block v-if="tramosIncompletos && tramosCompletos.length" strong class="aviso aviso-error">
        <p>{{ $t('misVacaciones.tramoIncompleto') }}</p>
      </f7-block>

      <!-- El error del cálculo se enseña aquí, en el sitio: es la respuesta a las fechas que se
           acaban de elegir, no un aviso suelto al final. Y solo si es el de ESAS fechas: ver
           `errorVigente`. -->
      <f7-block v-if="errorVigente" strong class="aviso aviso-error">
        <p>{{ errorVigente }}</p>
      </f7-block>

      <!-- Mientras el servidor recalcula no se enseña el cómputo anterior: serían los días, el
           saldo y los avisos de unas fechas que ya no están elegidas. Y «Enviar solicitud» está
           deshabilitado hasta que llegue (ver `vistaPreviaAlDia`). -->
      <f7-block v-if="calculando" strong class="aviso">
        <p>{{ $t('misVacaciones.calculando') }}</p>
      </f7-block>

      <template v-if="computoVigente">
        <f7-block-title>{{ $t('misVacaciones.diasQueSeDescuentan') }}: {{ computoVigente.diasQueDescuenta }}</f7-block-title>
        <f7-block strong class="chips">
          <span v-for="dia in computoVigente.dias" :key="dia" class="chip">{{ corta(dia) }}</span>
          <!-- Los que se caen van tachados y con su nombre: es lo que explica por qué un tramo de
               cinco días descuenta tres, sin tener que preguntarlo. -->
          <span
            v-for="excluido in computoVigente.diasExcluidos"
            :key="'x-' + excluido.fecha"
            class="chip chip-excluido"
            :title="excluido.detalle"
          >{{ corta(excluido.fecha) }} · {{ excluido.detalle }}</span>
          <p v-if="!computoVigente.dias.length && !computoVigente.diasExcluidos.length" class="vacio">
            {{ $t('misVacaciones.sinDias') }}
          </p>
        </f7-block>

        <!-- Saldo insuficiente es un error y no un aviso: el servidor rechazará el envío. Se dice
             aquí para no gastarle a nadie el viaje, y con SU MISMO texto: quien sabe qué parte del
             saldo sirve para ESAS fechas es el servidor (D2), y escribir aquí otra frase sería
             contarlo dos veces y poder contarlo distinto. -->
        <f7-block v-if="saldoInsuficiente" strong class="aviso aviso-error">
          <p>{{ computoVigente.avisoSaldo }}</p>
        </f7-block>

        <!-- Trabajo planificado: AVISO, no error. Se puede pedir igual. -->
        <f7-block v-if="trabajoPlanificado.length" strong class="aviso aviso-naranja">
          <p class="aviso-titulo">{{ $t('misVacaciones.trabajoPlanificado') }}</p>
          <p v-for="(t, i) in trabajoPlanificado" :key="'t-' + i" class="aviso-linea">
            {{ corta(t.fecha) }} · {{ t.trabajo }}<template v-if="t.cliente"> · {{ t.cliente }}</template>
          </p>
          <p class="aviso-pie">{{ $t('misVacaciones.trabajoPlanificadoPie') }}</p>
        </f7-block>
      </template>

      <f7-block-title>{{ $t('misVacaciones.comentarioAprobador') }}</f7-block-title>
      <f7-block strong>
        <textarea
          class="comentario"
          rows="3"
          :value="comentario"
          :placeholder="$t('misVacaciones.comentarioPlaceholder')"
          @input="comentario = $event.target.value"
        ></textarea>
      </f7-block>

      <!-- La autoconfirmación del único ADMIN es la única solicitud que nace confirmada, y el
           motivo es obligatorio: queda en rh_audit. Si no se pide aquí, el envío falla. -->
      <template v-if="autoconfirma">
        <f7-block-title>{{ $t('misVacaciones.motivoAutoconfirma') }}</f7-block-title>
        <f7-block strong>
          <textarea
            class="comentario"
            rows="2"
            :value="motivo"
            :placeholder="$t('misVacaciones.motivoPlaceholder')"
            @input="motivo = $event.target.value"
          ></textarea>
        </f7-block>
      </template>

      <!-- Las normas vigentes, desplegables, con su versión y su fecha. El texto lo publica la #56:
           aquí no se escribe ninguna norma a mano. -->
      <f7-block strong class="normas">
        <!-- Un fallo al leerlas NO es «no hay publicadas»: si se tratara como tal, la pantalla no
             enseñaría la casilla y el envío iría sin versión, y el servidor lo rechazaría sin que
             hubiera forma de marcarla. Se dice y se puede reintentar. -->
        <template v-if="estadoNormas === 'error'">
          <p class="normas-error">{{ $t('misVacaciones.errorNormas') }}</p>
          <f7-link @click="cargarNormas">{{ $t('misVacaciones.reintentarNormas') }}</f7-link>
        </template>
        <p v-else-if="estadoNormas === 'cargando'" class="sin-normas">
          {{ $t('misVacaciones.cargandoNormas') }}
        </p>
        <template v-else-if="normas">
          <div class="normas-cabecera" @click="normasAbiertas = !normasAbiertas">
            <span class="triangulo">{{ normasAbiertas ? '▾' : '▸' }}</span>
            {{ $t('misVacaciones.normas') }} · {{ $t('misVacaciones.version', { version: normas.version }) }}
            <template v-if="normas.fechaPublicacion"> · {{ fecha(normas.fechaPublicacion) }}</template>
          </div>
          <ol v-show="normasAbiertas" class="normas-lista">
            <li v-for="(norma, i) in normas.normas" :key="i">{{ norma }}</li>
          </ol>
          <label class="leidas">
            <input type="checkbox" :checked="normasLeidas" @change="normasLeidas = $event.target.checked" />
            {{ $t('misVacaciones.heLeido', { version: normas.version }) }}
          </label>
        </template>
        <!-- Mientras la #56 no haya publicado su versión 1 no hay texto que leer, así que no se
             puede exigir haberlo leído. Es el mismo criterio que aplica el servidor (D-NOR). -->
        <p v-else class="sin-normas">{{ $t('misVacaciones.sinNormas') }}</p>
      </f7-block>

      <f7-block strong class="pie-solicitud">
        <span class="disponibles">{{ $t('misVacaciones.disponiblesAhora') }}: <b>{{ disponiblesAhora }}</b></span>
        <span class="botones">
          <f7-button :text="$t('misVacaciones.volver')" class="display-flex" @click="$emit('cerrar')"></f7-button>
          <f7-button
            fill
            :text="$t('misVacaciones.enviar')"
            :disabled="!puedeEnviar"
            class="display-flex"
            @click="enviar"
          ></f7-button>
        </span>
        <!-- **Qué falta para poder enviar, junto al botón.** Es la misma regla de §9 que el botón
             «+ Solicitar vacaciones» de «Mis vacaciones»: un control que no se puede usar no se
             queda mudo. Aquí el motivo estaba repartido por la pantalla —el periodo a medias, el
             error del cálculo, la casilla de las normas— y el botón no decía cuál de ellos le
             tocaba a esa persona.

             Son frases cortas de lo que hay que HACER, no una segunda copia de esos avisos: la
             explicación se queda donde está, y aquí va el paso siguiente. Y salen de la misma
             lista que decide si el botón se puede pulsar (`motivosParaNoEnviar`), así que no se
             pueden desmentir. -->
        <p v-if="motivosParaNoEnviar.length" class="falta-para-enviar">
          {{ $t('misVacaciones.noSePuedeEnviar') }}
          <span v-for="(falta, i) in motivosParaNoEnviar" :key="i" class="falta">{{ falta }}</span>
        </p>
      </f7-block>
    </f7-page>
  </f7-popup>
</template>
<style scoped>
.tramos { padding: 8px 10px; }
.tramo-fila { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; margin-bottom: 8px; }
.campo { display: flex; flex-direction: column; flex: 1 1 140px; }
.campo-label { font-size: 11px; color: #64748b; }
.campo input {
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.quitar { font-size: 12px; color: #d9534f; }
.anadir { font-size: 13px; }

.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  display: inline-block;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  background-color: #fff;
}
.chip-excluido { text-decoration: line-through; color: #8e8e93; background-color: #f4f5f7; }

.aviso { border-left: 4px solid #cfd4da; }
.aviso p { margin: 3px 0; font-size: 13px; }
.aviso-error { background-color: #fdecea; border-left-color: #d9534f; color: #8a1c13; }
.aviso-naranja { background-color: #fff4e5; border-left-color: #f0a000; color: #8a4b00; }
.aviso-titulo { font-weight: bold; }
.aviso-pie { font-size: 12px; opacity: 0.85; }
.aviso-linea { font-size: 13px; }

.comentario {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: inherit;
}

.normas { border: 1px dashed #7b61ff; border-radius: 6px; }
.normas-cabecera { font-weight: bold; font-size: 13px; color: #1a2744; cursor: pointer; }
.triangulo { color: #7b61ff; }
.normas-lista { margin: 8px 0 8px 18px; padding: 0; font-size: 13px; line-height: 1.5; }
.normas-lista li { margin-bottom: 4px; }
.leidas { display: block; margin-top: 6px; font-size: 13px; font-weight: bold; }
.sin-normas { font-size: 13px; color: #8e8e93; margin: 0; }
.normas-error { font-size: 13px; color: #8a1c13; margin: 0 0 4px 0; }

.pie-solicitud {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}
.disponibles { font-size: 13px; color: #1a2744; }
.botones { display: flex; gap: 8px; }
/* Ocupa la línea entera del pie, debajo de los dos botones: en ancho de móvil el pie ya se
   envuelve, así que el motivo cae justo bajo «Enviar solicitud». */
.falta-para-enviar { flex: 1 1 100%; margin: 4px 0 0; font-size: 12px; color: #8a4b00; }
.falta { display: block; }
.vacio { font-size: 13px; color: #8e8e93; margin: 0; }
</style>
<script>
import RhSolicitudesVacaciones from "./../../js/db/RhSolicitudesVacaciones.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import soloMandaLaDeDelante, { MOTIVO_DESTRUCCION } from "./../../js/utils/soloMandaLaDeDelante.js";

/**
 * Formulario «Solicitar vacaciones» (#58). Uno o varios tramos, el cálculo en vivo de lo que se
 * descuenta, los avisos y las normas que hay que marcar antes de enviar.
 *
 * **El cálculo lo hace el servidor** (#57) y esta pantalla solo lo pinta: cada cambio de fecha es
 * una llamada a la vista previa. No se cuentan días aquí ni se decide qué festivo descuenta, porque
 * entonces habría dos versiones de D1 y la primera vez que discreparan nadie sabría cuál vale.
 *
 * Lo único que se decide en la pantalla es cuándo se puede pulsar «Enviar solicitud»; y se decide
 * en corto —hay días, hay saldo y están marcadas las normas— a sabiendas de que el servidor lo
 * vuelve a comprobar entero. Deshabilitar el botón ahorra el viaje; no es la validación.
 */
export default {
  // Las salidas de este formulario son las mismas tres de la pantalla que lo contiene y están
  // enumeradas en el mismo sitio (ver `soloMandaLaDeDelante`). La de salir de la pantalla no la ve
  // él —es un evento de la página—, así que se la pasa la ventana por su `ref`.
  mixins: [soloMandaLaDeDelante],
  props: {
    opened: { type: Boolean, default: false },
    /** El saldo del año que enseña «Mis vacaciones», para poder pintar el pie antes del primer cálculo. */
    disponibles: { type: Number, default: 0 },
    autoconfirma: { type: Boolean, default: false },
    /**
     * **El «cargando» de la pantalla que contiene este popup**, con sus propietarios por petición
     * (`cogerReloj` de `mis-vacaciones.vue`). Llega de fuera a propósito: el preloader de
     * Framework7 es un modal de la **aplicación** y no se cuenta, así que un registro propio aquí
     * sería otro vigilante del mismo modal sin hablarse con el de la ventana, y el que terminara
     * antes apagaría el reloj del otro. Eso es exactamente lo que costó las rondas 4 y 11.
     */
    reloj: { type: Object, required: true }
  },
  data: function() {
    return {
      tramos: [this.tramoVacio()],
      computo: null,
      /** Para qué fechas es el cálculo que hay en pantalla. Ver `vistaPreviaAlDia`. */
      computoDe: null,
      trabajoPlanificado: [],
      errorCalculo: null,
      /**
       * **De qué fechas es ese error.** Lo mismo que `computoDe` es para `computo`, y por el mismo
       * motivo: lo que el cálculo escribe se pinta si y solo si casa con lo que hay delante (ver
       * `errorVigente`). Sin firma había que borrarlo a mano en cada vía que cambia las fechas, y
       * la vía que se olvidaba —añadir un periodo— lo dejaba puesto.
       */
      errorCalculoDe: null,
      comentario: '',
      motivo: '',
      normas: null,
      /** `null` mientras se cargan y `'error'` si no se han podido leer. Ver `cargarNormas`. */
      estadoNormas: 'cargando',
      normasAbiertas: false,
      normasLeidas: false,
      // Si el envío está en marcha. Apaga el botón y escribe su frase, como cualquier otra
      // condición que lo apague (ver `motivosParaNoEnviar`). Lo suelta **el envío que lo cogió**,
      // por los dos caminos y sin mirar la sesión: ver `concluirEnvio`.
      enviando: false
    };
  },
  created: function() {
    // Teclear una fecha dispara varios `change` y las respuestas no vuelven en orden: sin el
    // carril, el cálculo de un tramo a medio escribir puede llegar después y pintarse encima.
    this.carril = carrilPeticiones();
    // Las normas van por su propio carril por lo mismo. El popup no se destruye al cerrarlo, así
    // que cerrar y volver a abrir mientras la primera lectura sigue en vuelo dejaba a las dos
    // escribiendo en el mismo sitio: la vieja podía poner una versión anterior —que el servidor
    // rechazaría al enviar— o marcar como fallida una carga nueva que había ido bien (revisión
    // CODEX del PR #1733).
    this.carrilNormas = carrilPeticiones();
    // El envío que está en vuelo, como testigo propio. NO es `enviando`: ese es lo que se pinta
    // —apaga el botón y escribe su frase— y esto es quién responde de soltarlo (ver
    // `concluirEnvio`). Fuera de `data` porque no se pinta.
    this.envioEnCurso = null;
  },
  computed: {
    tramosCompletos: function() {
      return this.tramos.filter(function(t) { return t.desde && t.hasta; });
    },
    /**
     * Si hay alguna fila de fechas sin terminar. Se dice en pantalla, porque el cálculo y el
     * envío solo mandan `tramosCompletos` y lo que se guardaría no serían las fechas que se ven.
     */
    tramosIncompletos: function() {
      return this.tramos.some(function(t) { return !t.desde || !t.hasta; });
    },
    /**
     * La firma de lo que hay en pantalla, para compararla con la de lo que se calculó.
     *
     * Con una fila a medias no hay firma: lo que se mandaría no son las fechas que se ven, así
     * que **ninguna** vista previa puede corresponderle.
     */
    firmaTramos: function() {
      return this.tramosIncompletos ? null : this.firmaDe(this.tramos);
    },
    /**
     * **Si el cálculo que hay en pantalla es el de las fechas que hay en pantalla.**
     *
     * Es la única condición que hace falta para dejar enviar, y sustituye a las tres que se
     * fueron añadiendo de una en una. Todas eran la misma cosa —lo previsualizado no es lo que
     * se iba a mandar— y cada ronda de revisión traía la siguiente: una fila a medias que el
     * envío se dejaba fuera en silencio, y después un tramo recién editado cuyo cálculo todavía
     * estaba en vuelo mientras la pantalla seguía enseñando los días, el saldo y los avisos de
     * las fechas anteriores (revisión CODEX del PR #1733, rondas 2 y 3).
     *
     * Comparando la firma se cierran las tres y las que vengan: mientras no haya llegado la
     * respuesta de ESTAS fechas, no hay vista previa que enseñar y no se envía.
     */
    vistaPreviaAlDia: function() {
      return !!this.computo && !!this.computoDe && this.computoDe === this.firmaTramos;
    },
    /**
     * El cálculo **que se puede pintar**: el de las fechas que hay en pantalla, o nada.
     *
     * Todo lo que la ventana enseña del cálculo —los chips de los días, los excluidos, el saldo,
     * los días que faltan y el aviso de trabajo planificado— sale de aquí y no de `computo`, que
     * es la última respuesta recibida sea de las fechas que sea. Con `computo` a secas, cambiar
     * un tramo dejaba en pantalla los días y el saldo de la selección anterior mientras llegaba
     * la respuesta nueva.
     */
    computoVigente: function() {
      return this.vistaPreviaAlDia ? this.computo : null;
    },
    /**
     * **El error del cálculo que se puede decir**: el de las fechas de ahora, o nada. Misma regla
     * que `vistaPreviaAlDia` y por lo mismo, extendida al último estado del cálculo que no tenía
     * firma propia.
     *
     * `errorCalculo` había que borrarlo a mano en cada vía que cambiara las fechas, y la vía que
     * se olvidaba dejaba a la vista el veredicto de unas fechas que ya no son las elegidas
     * —mientras el pie pide completar el periodo: §9 del `CLAUDE.md` del orquestador prohíbe que
     * dos partes de la misma pantalla digan lo contrario—. Con firma deja de pintarse solo, y
     * también cuando el fallo llega tarde: lo escribirá con SU firma, que ya no es la de ahora.
     */
    errorVigente: function() {
      if (!this.errorCalculo || this.errorCalculoDe === null) return null;
      return this.errorCalculoDe === this.firmaTramos ? this.errorCalculo : null;
    },
    /** Si se está esperando el cálculo de las fechas de ahora. Solo para avisarlo en pantalla. */
    calculando: function() {
      return !!this.tramosCompletos.length && !this.tramosIncompletos && !this.errorVigente
        && !this.vistaPreviaAlDia;
    },
    /** El saldo que manda es el del último cálculo: es el de ahora mismo, no el de al abrir. */
    disponiblesAhora: function() {
      const computo = this.computoVigente;
      return computo && computo.saldo && computo.saldo.saldo !== null
        && computo.saldo.saldo !== undefined
        ? computo.saldo.saldo : this.disponibles;
    },
    /**
     * Si los días pedidos **no caben**, según el servidor. No se compara `diasQueDescuenta` con
     * `disponibles`: el arrastre del año anterior está en el saldo mientras sigue vivo pero solo
     * paga días hasta el 31 de marzo (D2), así que una petición de abril con saldo de sobra en
     * febrero puede no caber. El veredicto lo da `cabenEnElSaldo`, que es el mismo que aplicará el
     * envío (`RhSaldoVacacionesService.cabida`), y el motivo viaja ya escrito en `avisoSaldo`.
     *
     * `cabenEnElSaldo` ausente —una respuesta vieja, o un servidor sin desplegar— **no es «no
     * caben»**: dejar el botón apagado por no haber recibido el campo sería inventar un error.
     */
    saldoInsuficiente: function() {
      const computo = this.computoVigente;
      return !!(computo && computo.cabenEnElSaldo === false && computo.avisoSaldo);
    },
    /**
     * **Qué le falta para poder enviar**, en frases cortas de lo que hay que hacer y en el orden
     * en que se hace: primero las fechas, después las normas y al final el motivo.
     *
     * Esta lista es la que decide si «Enviar solicitud» se puede pulsar (ver `puedeEnviar`), y
     * está así a propósito: antes el botón se apagaba con una condición y el texto de la pantalla
     * se escribía aparte, así que había condiciones que apagaban el botón **sin decir nada** —el
     * motivo obligatorio de la autoconfirmación, las normas sin marcar, el formulario recién
     * abierto— y al pulsar «no pasaba nada». Es el defecto que dirección registró el 05/10/2026
     * (§9 del `CLAUDE.md` del orquestador) y volvió a encontrar el 10/10/2026 en el botón «+
     * Solicitar vacaciones» de «Mis vacaciones». Colgando las dos cosas de la misma lista, una
     * condición nueva no puede quedarse muda: para apagar el botón tiene que escribir su frase.
     *
     * No repite los avisos que ya están en su sitio —el periodo a medias, el error del cálculo,
     * el saldo— porque la causa se dice una vez; lo que se dice aquí es el paso siguiente.
     */
    motivosParaNoEnviar: function() {
      const faltas = [];
      // 0 · Ya se está enviando. Va primero y corta: mientras el servidor contesta no falta nada
      // más, y lo único que hay que decir es que espere. Sin esto, el botón seguía habilitado
      // durante todo el POST y un doble toque creaba DOS solicitudes idénticas; la que perdía
      // escribía su error después de que la otra hubiera ido bien (revisión CODEX del PR #1733,
      // r4229768214).
      if (this.enviando) {
        return [this.$t('misVacaciones.faltaEsperarElEnvio')];
      }
      // 1 · Las fechas y su cálculo. Encadenados porque son estados del mismo paso: sin fechas no
      // hay periodo a medias, y sin periodo completo no hay cálculo que esperar.
      if (!this.tramosCompletos.length) {
        faltas.push(this.$t('misVacaciones.faltaElegirFechas'));
      } else if (this.tramosIncompletos) {
        faltas.push(this.$t('misVacaciones.faltaCompletarPeriodo'));
      } else if (this.errorVigente) {
        faltas.push(this.$t('misVacaciones.faltaCorregirFechas'));
      } else if (!this.vistaPreviaAlDia) {
        // Lo que se envía es exactamente lo que se ha previsualizado, o no se envía.
        faltas.push(this.$t('misVacaciones.faltaEsperarElCalculo'));
      } else if (this.saldoInsuficiente) {
        faltas.push(this.$t('misVacaciones.faltaSaldo'));
      } else if (!(this.computo.diasQueDescuenta > 0)) {
        faltas.push(this.$t('misVacaciones.faltanDiasQueDescontar'));
      }
      // 2 · Las normas (D-NOR). Sin ninguna publicada no hay nada que marcar y no falta nada, pero
      // «no hay publicadas» tiene que ser una respuesta del servidor y no un fallo al preguntar
      // (ver `cargarNormas`). Cualquier estado que no sea `ok` frena, no solo los dos que hay hoy:
      // un estado nuevo no puede dejar enviar por el hecho de no estar nombrado aquí.
      if (this.estadoNormas === 'error') {
        faltas.push(this.$t('misVacaciones.faltaReintentarNormas'));
      } else if (this.estadoNormas !== 'ok') {
        faltas.push(this.$t('misVacaciones.faltaCargarNormas'));
      } else if (this.normas && !this.normasLeidas) {
        faltas.push(this.$t('misVacaciones.faltaMarcarNormas'));
      }
      // 3 · El motivo del único ADMIN que se autoconfirma: obligatorio, y queda en rh_audit.
      if (this.autoconfirma && !this.motivo.trim()) {
        faltas.push(this.$t('misVacaciones.faltaElMotivo'));
      }
      return faltas;
    },
    puedeEnviar: function() {
      return this.motivosParaNoEnviar.length === 0;
    }
  },
  methods: {
    tramoVacio: function() {
      return { desde: null, hasta: null };
    },
    /**
     * **Este formulario deja de mandar sobre lo que tiene en vuelo**, por cualquiera de las tres
     * salidas que enumera `soloMandaLaDeDelante`: cerrar sesión, salir de la pantalla que lo
     * contiene —que se la pasa la ventana, porque es un evento de la página— y destruirse.
     *
     * Las **dos lecturas** se tiran siempre. El popup no se destruye al cerrarlo ni al cerrar
     * sesión —la ruta es `keepAlive`—, así que sin esto el cálculo o las normas que estuvieran en
     * vuelo seguían siendo los vigentes de sus carriles y podían escribir aquí el error de unas
     * fechas de otra cuenta, o las normas que leyó aquella, hasta que alguien volviera a abrirlo.
     *
     * El **envío** no se toca más que para soltar el reloj al destruirse, y las dos cosas son a
     * propósito:
     *
     *  - el reloj es un modal de la **aplicación**, así que un dueño que ya no existe lo dejaría
     *    puesto encima de la pantalla siguiente, con su fondo tapándolo todo y nadie que pueda
     *    apagarlo. Al salir de la pantalla o al cerrar sesión no hace falta hacerlo aquí: el
     *    registro es de la ventana y ella lo suelta entero (`olvidarTodasLasCargas`);
     *  - `enviando` **no** se suelta por ninguna de las tres. Lo suelta el envío que lo cogió
     *    (`concluirEnvio`), y el que dejó una sesión que ya no está lo suelta `abrir()` al
     *    reabrir. Soltarlo desde fuera con el POST todavía vivo encendía un botón que el
     *    adaptador rechaza como repetido: un botón que al pulsarlo no hace nada, que es justo lo
     *    que prohíbe §9 del `CLAUDE.md` del orquestador (revisión CODEX del PR #1733, ronda 6).
     *
     * El aviso de la solicitud enviada sí sale aunque se haya salido de la pantalla, y también es
     * a propósito: es el resultado de algo que esa persona hizo, y lo que decide si le corresponde
     * es la **sesión** y no qué pantalla esté mirando (`deLaSesionQueLaLanzo`).
     */
    dejarDeMandar: function(motivo) {
      this.olvidarCalculo();
      this.carrilNormas.invalidar();
      if (motivo === MOTIVO_DESTRUCCION && this.envioEnCurso) {
        this.concluirEnvio(this.envioEnCurso);
      }
    },
    /**
     * Al abrir se vuelve a empezar. El popup vive dentro de la pantalla y no se destruye al
     * cerrarlo, así que sin esto la segunda solicitud saldría con las fechas, el comentario y la
     * casilla de la primera ya puestos.
     */
    abrir: function() {
      // Lo que quedara en vuelo de la apertura anterior ya no manda sobre nada de esta: su
      // respuesta llegaría a un formulario que ya se ha vuelto a empezar. El cálculo y su carril
      // se olvidan por el mismo sitio que cuando se borra una fecha (ver `olvidarCalculo`): con el
      // estado escrito a mano aquí, cualquier campo nuevo habría que acordarse de limpiarlo dos
      // veces.
      this.olvidarCalculo();
      this.carrilNormas.invalidar();
      this.tramos = [this.tramoVacio()];
      this.comentario = '';
      this.motivo = '';
      this.normas = null;
      this.normasLeidas = false;
      this.normasAbiertas = false;
      // `enviando` NO se reinicia aquí a propósito, aunque todo lo demás sí: lo suelta el propio
      // envío al terminar, y es lo único que está en manos del servidor y no de este formulario.
      // Poniéndolo a `false` con un envío todavía en vuelo, el botón se encendería y el adaptador
      // cortaría la petición repetida en silencio: un botón que al pulsarlo no hace nada, que es
      // justo lo que §9 prohíbe. Con el cerrojo puesto, el pie dice que espere.
      //
      // **Pero un envío de OTRA sesión sí se suelta aquí.** Si la persona cerró sesión con el POST
      // a medias y ese POST nunca contesta —se queda colgado—, nadie más va a soltar su cerrojo, y
      // quien entre después se encuentra «ya se está enviando» para siempre. El cerrojo del
      // adaptador es por sesión, así que soltarlo no deja salir dos peticiones.
      this.soltarEnvioDeOtraSesion();
      this.cargarNormas();
    },
    /**
     * Las normas vigentes. **Un fallo al preguntarlas no es «no hay publicadas»**: pasándolo por
     * `normas = null`, la pantalla afirmaba que no hay normas, no enseñaba la casilla y dejaba
     * enviar con `versionNormasLeida: null`; si sí había una versión vigente el servidor
     * rechazaba el envío sin remedio, porque no había forma de marcarla sin cerrar y volver a
     * abrir el formulario (revisión CODEX del PR #1733). Así que el fallo se dice y se puede
     * reintentar, y hasta entonces no se envía.
     *
     * Va por su carril: lo que conteste una lectura que ya no es la vigente no se pinta. Ver
     * `carrilNormas`.
     */
    cargarNormas: function() {
      const self = this;
      self.estadoNormas = 'cargando';
      self.carrilNormas.lanzar(RhSolicitudesVacaciones.normasVigentes(), {
        exito: function(res) {
          const versionAnterior = self.normas ? self.normas.version : null;
          self.normas = res && res.version ? res : null;
          // La casilla dice «he leído la versión N»: si la lectura trae otra versión, deja de
          // estar marcada. Lo que se envía es `normas.version`, así que mantenerla marcada
          // declararía por leída una versión que nadie ha visto.
          if (!self.normas || self.normas.version !== versionAnterior) {
            self.normasLeidas = false;
          }
          self.estadoNormas = 'ok';
        },
        limpiar: function() {
          // La casilla que hubiera marcada era de la versión que ya no se puede confirmar: se
          // quita con las normas, o se enviaría «he leído la versión N» sin tener la N.
          self.normas = null;
          self.normasLeidas = false;
          self.estadoNormas = 'error';
        }
      });
    },
    fijar: function(indice, campo, evento) {
      const tramo = Object.assign({}, this.tramos[indice]);
      tramo[campo] = evento.target.value || null;
      this.$set(this.tramos, indice, tramo);
      this.calcular();
    },
    /**
     * Añadir un periodo **no lanza cálculo**: el tramo nace vacío, así que no hay nada que
     * calcular hasta que se rellene. Y tampoco tiene que limpiar nada: lo que hubiera calculado
     * —los días y el error— deja de pintarse solo, porque la firma de las fechas ha cambiado (ver
     * `errorVigente` y `vistaPreviaAlDia`).
     */
    anadirTramo: function() {
      this.tramos.push(this.tramoVacio());
    },
    quitarTramo: function(indice) {
      this.tramos.splice(indice, 1);
      this.calcular();
    },
    /** La firma de unos tramos: las dos fechas de cada uno, en orden. */
    firmaDe: function(tramos) {
      return JSON.stringify(tramos.map(function(t) { return [t.desde, t.hasta]; }));
    },
    /**
     * El cálculo que se pinta: los días, el saldo y los avisos de trabajo. **No toca el error**,
     * que lo pone el `fallo` del carril justo después de limpiar.
     */
    olvidarComputo: function() {
      this.computo = null;
      this.computoDe = null;
      this.trabajoPlanificado = [];
    },
    /**
     * Lo anterior, el error **y la petición que estuviera en vuelo**, que es lo que de verdad
     * hace falta cuando ya no hay nada que calcular.
     *
     * Las tres cosas juntas y en un solo sitio a propósito: limpiar el estado sin invalidar el
     * carril dejaba a esa petición siendo la vigente, así que al fallar su manejador volvía a
     * poner `errorCalculo` con el error de unas fechas que ya no estaban seleccionadas, y el
     * mensaje se quedaba puesto hasta completar otro tramo o reabrir el formulario (revisión CODEX
     * del PR #1733, ronda 5). Es el mismo olvido que `cargarSolicitudDelDia` del calendario ya
     * evitaba invalidando también cuando el día abierto no es de vacaciones.
     */
    olvidarCalculo: function() {
      this.carril.invalidar();
      this.olvidarComputo();
      this.errorCalculo = null;
      this.errorCalculoDe = null;
    },
    calcular: function() {
      const self = this;
      // De qué fechas va a ser esta respuesta. Se guarda con el cálculo —y con el error— para que
      // la pantalla sepa si lo que enseña sigue siendo lo que se enviaría (ver `vistaPreviaAlDia`
      // y `errorVigente`).
      //
      // Sin firma no se pide nada: con una fila a medias, lo que se mandaría no son las fechas que
      // se ven, así que ninguna respuesta podría pintarse. Antes se pedía igual —solo con los
      // tramos completos— y esa petición nacía ya descartada.
      const firma = self.firmaTramos;
      if (!firma) {
        self.olvidarCalculo();
        return;
      }
      // **El error de antes se va al lanzar, no al contestar.** Es la respuesta a unas fechas que
      // ya no son las elegidas: dejándolo puesto, la pantalla seguía diciendo «ya tienes vacaciones
      // esos días» de las fechas anteriores mientras el servidor calculaba las corregidas, y encima
      // sin el «calculando», que depende de que no haya error. En una conexión lenta eso es la
      // pantalla afirmando que lo que acabas de arreglar sigue mal (§9 del `CLAUDE.md` del
      // orquestador: dos partes de la misma pantalla no pueden decir lo contrario).
      //
      // Los días y el saldo no hace falta borrarlos aquí: `computoVigente` ya no los pinta en
      // cuanto la firma deja de casar. Borrar el error es para que no quede guardado de unas
      // fechas a otras; lo que GARANTIZA que no se pinte es su firma (`errorVigente`), que no
      // depende de que nadie se acuerde.
      self.errorCalculo = null;
      self.errorCalculoDe = null;
      self.carril.lanzar(RhSolicitudesVacaciones.vistaPrevia(self.tramosCompletos), {
        exito: function(res) {
          // El error ya se limpió al lanzar, y nadie puede haberlo puesto mientras: el `fallo` solo
          // corre para la petición vigente, y las de antes pasan por `descartada`.
          self.computo = (res && res.computo) || null;
          self.computoDe = self.computo ? firma : null;
          self.trabajoPlanificado = (res && res.trabajoPlanificado) || [];
        },
        limpiar: function() {
          // Lo que queda en pantalla si falla es el cálculo de las fechas ANTERIORES, y entonces el
          // pie diría «se descuentan 2» para unas fechas que nadie ha calculado.
          self.olvidarComputo();
        },
        fallo: function(e) {
          // El mensaje del servidor es el que explica qué pasa con ESAS fechas («el año siguiente
          // se pide desde el 1 de octubre», «ya tienes vacaciones esos días»): se enseña tal cual,
          // donde se eligieron, y **diciendo de qué fechas es**. Salvo el de un fallo no previsto,
          // que es texto de Java y no una explicación (#829, ver `mensajeDelServidor`).
          self.errorCalculo = mensajeDelServidor(e, self.$t('misVacaciones.errorCalculando'));
          self.errorCalculoDe = firma;
        }
      });
    },
    /**
     * **El envío va atado a la sesión que lo lanzó.** El popup vive dentro de una ruta `keepAlive`
     * y cerrar sesión no lo destruye, así que el `then` de este POST se ejecutaba sobre la sesión
     * siguiente: quien entrara después en el mismo dispositivo veía un aviso con el código de
     * solicitud de la persona anterior, se le cerraba el formulario que tuviera abierto y se le
     * recargaba la pantalla. Quién decide eso es el adaptador —el mismo sello que ya usa la ventana
     * para sus lecturas—, no este manejador: ver `deLaSesionQueLaLanzo` en
     * `RhSolicitudesVacaciones.js` (revisión CODEX del PR #1733, ronda 10).
     *
     * **Y un envío a la vez.** `enviando` es lo que apaga el botón —escribiendo su frase, como
     * cualquier otra condición de `motivosParaNoEnviar`— y la guarda de aquí es la que corta el
     * doble toque que llegue antes de que Vue repinte. Que la petición no salga dos veces lo
     * garantiza además el adaptador (`unaSolaEnVuelo`), que es lo que cubre a quien se olvide.
     *
     * **Y el reloj se suelta antes de avisar, no después.** El preloader de Framework7 es uno de
     * la aplicación y no se cuenta por quien lo pide: un `hide()` apaga el que haya puesto
     * cualquiera. Apagándolo en un `then` final —después del `enviada`, que arranca la recarga del
     * resumen y enciende el suyo— el popup se cerraba y quedaban a la vista el saldo y las
     * solicitudes de ANTES del envío, sin reloj y como si ya fueran los definitivos (revisión
     * CODEX del PR #1733, ronda 11). La regla, que es la misma en las tres peticiones del módulo:
     * **cada una apaga el reloj que encendió ella y lo suelta antes de avisar a nadie; ninguna
     * apaga el de otra.** Soltar y volver a encender pasa en el mismo turno, así que no parpadea.
     * El reloj es el de la ventana que contiene el popup (la prop `reloj`), y se coge con testigo
     * para que concluir este envío no apague el de una lectura que siga en vuelo.
     */
    enviar: function() {
      const self = this;
      const app = self.$f7;
      if (self.enviando) return;
      self.enviando = true;
      // El testigo de ESTE envío: con qué sesión salió y qué reloj cogió. Es lo que `concluirEnvio`
      // mira para soltar lo suyo y solo lo suyo.
      const envio = {
        sesion: RhSolicitudesVacaciones.sesionActual(),
        reloj: self.reloj.coger('enviar')
      };
      self.envioEnCurso = envio;
      RhSolicitudesVacaciones.solicitar({
        tramos: self.tramosCompletos,
        comentario: self.comentario.trim() || null,
        versionNormasLeida: self.normas ? self.normas.version : null,
        motivo: self.motivo.trim() || null
      })
        .then(function(solicitud) {
          self.concluirEnvio(envio);
          app.toast.create({
            text: self.$t('misVacaciones.enviada', { codigo: (solicitud && solicitud.codigo) || '' }),
            position: 'center',
            closeTimeout: 3000
          }).open();
          // Lo último: quien escuche esto recarga el resumen, y el reloj de esa recarga es suyo.
          self.$emit('enviada', solicitud);
        })
        .catch(function(e) {
          // Termina por el mismo sitio que el camino bueno, también cuando la respuesta es de otra
          // sesión: el cerrojo del botón es de este envío y lo suelta este envío; el reloj, solo si
          // sigue siendo suyo (ver `concluirEnvio`).
          self.concluirEnvio(envio);
          // Y si esto es un intento repetido que el adaptador ha cortado, o la respuesta es de
          // otra sesión, no hay nada que decirle a quien está delante: el aviso era para quien
          // envió. Tampoco se vuelve a leer nada: el POST de verdad puede seguir en vuelo.
          if (RhSolicitudesVacaciones.sinNadaQueDecir(e)) return;
          // **Lo que se mandó ya no vale, así que se vuelve a leer antes de avisar.** Es el mismo
          // defecto que la ventana de registrar en nombre de otra persona (revisión CODEX del PR
          // #1737, ronda 7) y aquí no lo ha señalado nadie todavía: el servidor rechaza el envío si
          // la versión de las normas que se dice leída ya no es la vigente
          // (`exigirNormasLeidas`), y conservando las normas anteriores la casilla se quedaba
          // marcada sobre la versión de antes, así que cada reintento mandaba la misma versión
          // caduca y no había forma de salir sin cerrar y reabrir el formulario. El cálculo se tira
          // por lo mismo: lo que se envía es lo previsualizado, y el servidor acaba de decir que
          // esa foto no sirve.
          self.olvidarCalculo();
          self.calcular();
          self.cargarNormas();
          app.dialog.alert(mensajeDelServidor(e, self.$t('misVacaciones.errorEnviando')));
        });
    },
    /**
     * **Un envío termina en un solo sitio**, por los dos caminos, y suelta dos cosas que tienen
     * dueños distintos. Confundirlas es el hallazgo de la ronda 1 de esta tanda (r4241397173):
     *
     *  - el **cerrojo del botón** (`enviando`) es de ESTE envío y lo suelta él, sin mirar la
     *    sesión. Colgándolo de que la sesión siguiera siendo la que envió, cerrar sesión con el
     *    POST a medias lo dejaba puesto: la ruta lleva `keepAlive` y `abrir()` tampoco lo reinicia,
     *    así que el formulario se quedaba diciendo «ya se está enviando» y «Enviar solicitud» no
     *    hacía nada —justo lo que prohíbe §9 del `CLAUDE.md` del orquestador—. Y soltarlo no deja
     *    salir dos peticiones: de eso responde `unaSolaEnVuelo`, que ya es por sesión;
     *  - el **reloj** es un modal de la aplicación y lo suelta su testigo: si ya no es suyo —lo
     *    relevó otra petición de la ventana, o se tiró el registro al cambiar de sesión— no lo
     *    toca, porque el que hay puesto es el de la primera carga de quien acaba de entrar y
     *    apagarlo le deja la pantalla vacía y muda (ronda 12, y ronda 4 de esta tanda).
     *
     * El testigo propio es lo que evita el fallo simétrico: la respuesta de un envío de la cuenta
     * anterior no suelta el cerrojo de un envío que haya empezado quien entra después.
     */
    concluirEnvio: function(envio) {
      this.reloj.soltar(envio.reloj);
      if (this.envioEnCurso !== envio) return;
      this.envioEnCurso = null;
      this.enviando = false;
    },
    /**
     * El cerrojo que dejó un envío de otra sesión. Lo llama `abrir()`: si ese POST nunca contesta
     * no hay quien suelte el cerrojo, y quien entre después se encuentra el botón apagado
     * diciéndole que espere a un envío que no es suyo y que nunca va a terminar.
     */
    soltarEnvioDeOtraSesion: function() {
      const envio = this.envioEnCurso;
      if (!envio || envio.sesion === RhSolicitudesVacaciones.sesionActual()) return;
      this.concluirEnvio(envio);
    },
    /** «3 nov». Se compone del texto ISO sin pasar por `Date`, para que no se mueva de día. */
    corta: function(iso) {
      if (!iso) return '';
      const trozos = String(iso).substring(0, 10).split('-');
      if (trozos.length !== 3) return String(iso);
      return parseInt(trozos[2], 10) + ' ' + this.$t('misVacaciones.mes_' + parseInt(trozos[1], 10));
    },
    /** La fecha de publicación de las normas sí es un instante, así que va por `Date`. */
    fecha: function(valor) {
      if (!valor) return '';
      const d = new Date(valor);
      if (isNaN(d.getTime())) return String(valor);
      return ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2)
        + '/' + d.getFullYear();
    }
  }
};
</script>
